import QueryString from 'query-string';
import { toastr } from 'react-redux-toastr';
import {
  FLAG_GETTING_BANK_RECONCILIATIONS,
  GET_BANK_RECONCILIATIONS,
  CLEAR_BANK_RECONCILIATIONS,
  FLAG_GETTING_BANK_RECONCILIATION_LIQUIDATIONS,
  GET_BANK_RECONCILIATION_LIQUIDATIONS,
  CLEAR_BANK_RECONCILIATION_LIQUIDATIONS,
  FLAG_GETTING_BANK_RECONCILIATION_DEPOSITS,
  GET_BANK_RECONCILIATION_DEPOSITS,
  CLEAR_BANK_RECONCILIATION_DEPOSITS,
  FLAG_GETTING_BANK_RECONCILIATION_BANK_SUMMARY,
  GET_BANK_RECONCILIATION_BANK_SUMMARY,
  CLEAR_BANK_RECONCILIATION_BANK_SUMMARY,
  FLAG_GETTING_BANK_RECONCILIATION_CORPORATES,
  GET_BANK_RECONCILIATION_CORPORATES,
  CLEAR_BANK_RECONCILIATION_CORPORATES,
  FLAG_GETTING_BANK_RECONCILIATION_CORPORATE_MANUAL_VOUCHERS,
  GET_BANK_RECONCILIATION_CORPORATE_MANUAL_VOUCHERS,
  CLEAR_BANK_RECONCILIATION_CORPORATE_MANUAL_VOUCHERS,
  FLAG_GETTING_BANK_RECONCILIATION_MANUAL_VOUCHER_PAYMENTS,
  GET_BANK_RECONCILIATION_MANUAL_VOUCHER_PAYMENTS,
  CLEAR_BANK_RECONCILIATION_MANUAL_VOUCHER_PAYMENTS,
} from '../types';
import {
  DEPOSIT_COVERAGE_ENDPOINT,
  DEPOSIT_COVERAGE_BANK_SUMMARY_ENDPOINT,
  generateDepositCoverageLiquidationEndpoint,
  generateDepositCoverageCorporateEndpoint,
  generateDepositCoverageCorporateManualVoucherEndpoint,
  generateDepositCoverageManualVoucherPaymentEndpoint,
  generateManualVoucherPaymentSapMigrationRetryEndpoint,
  generateDepositCoverageDepositEndpoint,
  generateDepositSapMigrationRetryEndpoint,
} from '../../config/endpoints';
import { DEFAULT_GET_CONFIG, DEFAULT_POST_CONFIG } from '../../config/rest';
import { isErrorResponse } from '../../utils/error-handlers';

const buildCoverageFilters = ({ from, to, bankId, agencyId, status } = {}) => ({
  from: from || undefined,
  to: to || undefined,
  bankId: bankId || undefined,
  agencyId: agencyId || undefined,
  status: status && status.length ? status : undefined,
});

const flagGettingBankReconciliations = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_GETTING_BANK_RECONCILIATIONS,
    payload: flag,
  });

const getBankReconciliations =
  ({ page, size, ...filters } = {}) =>
  async (dispatch) => {
    try {
      dispatch(flagGettingBankReconciliations(true));

      const query = {
        ...buildCoverageFilters(filters),
        page,
        size,
      };

      const url = `${DEPOSIT_COVERAGE_ENDPOINT}?${QueryString.stringify(
        query,
      )}`;

      const response = await fetch(url, { ...DEFAULT_GET_CONFIG });

      await isErrorResponse(response, null, dispatch);

      const bankReconciliations = await response.json();

      dispatch({
        type: GET_BANK_RECONCILIATIONS,
        payload: bankReconciliations,
      });
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagGettingBankReconciliations(false));
    }
  };

const clearBankReconciliations = () => (dispatch) =>
  dispatch({
    type: CLEAR_BANK_RECONCILIATIONS,
  });

const flagGettingBankReconciliationLiquidations =
  ({ date, flag }) =>
  (dispatch) =>
    dispatch({
      type: FLAG_GETTING_BANK_RECONCILIATION_LIQUIDATIONS,
      payload: { date, flag },
    });

const getBankReconciliationLiquidations =
  ({ date, ...filters }) =>
  async (dispatch) => {
    try {
      dispatch(flagGettingBankReconciliationLiquidations({ date, flag: true }));

      const query = buildCoverageFilters(filters);

      const url = `${generateDepositCoverageLiquidationEndpoint(
        date,
      )}?${QueryString.stringify(query)}`;

      const response = await fetch(url, { ...DEFAULT_GET_CONFIG });

      await isErrorResponse(response, null, dispatch);

      const liquidations = await response.json();

      dispatch({
        type: GET_BANK_RECONCILIATION_LIQUIDATIONS,
        payload: { date, content: liquidations },
      });
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(
        flagGettingBankReconciliationLiquidations({ date, flag: false }),
      );
    }
  };

const clearBankReconciliationLiquidations =
  ({ date }) =>
  (dispatch) =>
    dispatch({
      type: CLEAR_BANK_RECONCILIATION_LIQUIDATIONS,
      payload: { date },
    });

const flagGettingBankReconciliationCorporates =
  ({ date, flag }) =>
  (dispatch) =>
    dispatch({
      type: FLAG_GETTING_BANK_RECONCILIATION_CORPORATES,
      payload: { date, flag },
    });

const getBankReconciliationCorporates =
  ({ date }) =>
  async (dispatch) => {
    try {
      dispatch(flagGettingBankReconciliationCorporates({ date, flag: true }));

      const url = generateDepositCoverageCorporateEndpoint(date);

      const response = await fetch(url, { ...DEFAULT_GET_CONFIG });

      await isErrorResponse(response, null, dispatch);

      const corporates = await response.json();

      dispatch({
        type: GET_BANK_RECONCILIATION_CORPORATES,
        payload: { date, content: corporates },
      });
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagGettingBankReconciliationCorporates({ date, flag: false }));
    }
  };

const clearBankReconciliationCorporates =
  ({ date }) =>
  (dispatch) =>
    dispatch({
      type: CLEAR_BANK_RECONCILIATION_CORPORATES,
      payload: { date },
    });

const flagGettingBankReconciliationCorporateManualVouchers =
  ({ corporate, flag }) =>
  (dispatch) =>
    dispatch({
      type: FLAG_GETTING_BANK_RECONCILIATION_CORPORATE_MANUAL_VOUCHERS,
      payload: { ...corporate, flag },
    });

const getBankReconciliationCorporateManualVouchers =
  ({ date, holderType, corporateId, currency }) =>
  async (dispatch) => {
    const corporate = { date, holderType, corporateId, currency };

    try {
      dispatch(
        flagGettingBankReconciliationCorporateManualVouchers({
          corporate,
          flag: true,
        }),
      );

      const query = { currency: currency || undefined };

      const url = `${generateDepositCoverageCorporateManualVoucherEndpoint({
        date,
        holderType,
        corporateId,
      })}?${QueryString.stringify(query)}`;

      const response = await fetch(url, { ...DEFAULT_GET_CONFIG });

      await isErrorResponse(response, null, dispatch);

      const manualVouchers = await response.json();

      dispatch({
        type: GET_BANK_RECONCILIATION_CORPORATE_MANUAL_VOUCHERS,
        payload: { ...corporate, content: manualVouchers },
      });
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(
        flagGettingBankReconciliationCorporateManualVouchers({
          corporate,
          flag: false,
        }),
      );
    }
  };

const clearBankReconciliationCorporateManualVouchers =
  ({ date, holderType, corporateId, currency }) =>
  (dispatch) =>
    dispatch({
      type: CLEAR_BANK_RECONCILIATION_CORPORATE_MANUAL_VOUCHERS,
      payload: { date, holderType, corporateId, currency },
    });

const flagGettingBankReconciliationManualVoucherPayments =
  ({ manualVoucherId, flag }) =>
  (dispatch) =>
    dispatch({
      type: FLAG_GETTING_BANK_RECONCILIATION_MANUAL_VOUCHER_PAYMENTS,
      payload: { manualVoucherId, flag },
    });

const getBankReconciliationManualVoucherPayments =
  ({ manualVoucherId }) =>
  async (dispatch) => {
    try {
      dispatch(
        flagGettingBankReconciliationManualVoucherPayments({
          manualVoucherId,
          flag: true,
        }),
      );

      const url = generateDepositCoverageManualVoucherPaymentEndpoint(
        manualVoucherId,
      );

      const response = await fetch(url, { ...DEFAULT_GET_CONFIG });

      await isErrorResponse(response, null, dispatch);

      const payments = await response.json();

      dispatch({
        type: GET_BANK_RECONCILIATION_MANUAL_VOUCHER_PAYMENTS,
        payload: { manualVoucherId, content: payments },
      });
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(
        flagGettingBankReconciliationManualVoucherPayments({
          manualVoucherId,
          flag: false,
        }),
      );
    }
  };

const clearBankReconciliationManualVoucherPayments =
  ({ manualVoucherId }) =>
  (dispatch) =>
    dispatch({
      type: CLEAR_BANK_RECONCILIATION_MANUAL_VOUCHER_PAYMENTS,
      payload: { manualVoucherId },
    });

const retryManualVoucherPaymentSapMigration =
  ({ manualVoucherPaymentId, manualVoucherId, corporate }) =>
  async (dispatch) => {
    try {
      const url = generateManualVoucherPaymentSapMigrationRetryEndpoint(
        manualVoucherPaymentId,
      );

      const response = await fetch(url, { ...DEFAULT_POST_CONFIG });

      await isErrorResponse(response, null, dispatch);

      const { migrationStatus, sapErrorMessage } = await response.json();

      if (migrationStatus === 'FAILED') {
        toastr.error('Error', sapErrorMessage || 'Falló la migración a SAP');
      } else {
        toastr.success('Éxito', 'Pago enviado a SAP correctamente');
      }

      // pendingSapMigration of every parent row may change after a retry
      dispatch(getBankReconciliationManualVoucherPayments({ manualVoucherId }));
      dispatch(getBankReconciliationCorporateManualVouchers(corporate));
      dispatch(getBankReconciliationCorporates({ date: corporate.date }));
    } catch (error) {
      toastr.error('Error', error.message);
    }
  };

const flagGettingBankReconciliationDeposits =
  ({ liquidationId, flag }) =>
  (dispatch) =>
    dispatch({
      type: FLAG_GETTING_BANK_RECONCILIATION_DEPOSITS,
      payload: { liquidationId, flag },
    });

const getBankReconciliationDeposits =
  ({ liquidationId }) =>
  async (dispatch) => {
    try {
      dispatch(
        flagGettingBankReconciliationDeposits({ liquidationId, flag: true }),
      );

      const url = generateDepositCoverageDepositEndpoint(liquidationId);

      const response = await fetch(url, { ...DEFAULT_GET_CONFIG });

      await isErrorResponse(response, null, dispatch);

      const deposits = await response.json();

      dispatch({
        type: GET_BANK_RECONCILIATION_DEPOSITS,
        payload: { liquidationId, content: deposits },
      });
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(
        flagGettingBankReconciliationDeposits({ liquidationId, flag: false }),
      );
    }
  };

const clearBankReconciliationDeposits =
  ({ liquidationId }) =>
  (dispatch) =>
    dispatch({
      type: CLEAR_BANK_RECONCILIATION_DEPOSITS,
      payload: { liquidationId },
    });

const flagGettingBankReconciliationBankSummary = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_GETTING_BANK_RECONCILIATION_BANK_SUMMARY,
    payload: flag,
  });

const getBankReconciliationBankSummary =
  ({ from, to, bankId, agencyId } = {}) =>
  async (dispatch) => {
    try {
      dispatch(flagGettingBankReconciliationBankSummary(true));

      const query = {
        from: from || undefined,
        to: to || undefined,
        bankId: bankId || undefined,
        agencyId: agencyId || undefined,
      };

      const url = `${DEPOSIT_COVERAGE_BANK_SUMMARY_ENDPOINT}?${QueryString.stringify(
        query,
      )}`;

      const response = await fetch(url, { ...DEFAULT_GET_CONFIG });

      await isErrorResponse(response, null, dispatch);

      const bankSummary = await response.json();

      dispatch({
        type: GET_BANK_RECONCILIATION_BANK_SUMMARY,
        payload: bankSummary,
      });
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagGettingBankReconciliationBankSummary(false));
    }
  };

const clearBankReconciliationBankSummary = () => (dispatch) =>
  dispatch({
    type: CLEAR_BANK_RECONCILIATION_BANK_SUMMARY,
  });

const retrySapMigration =
  ({ depositId, liquidationId, documentType }) =>
  async (dispatch) => {
    try {
      const query = documentType
        ? `?${QueryString.stringify({ documentType })}`
        : '';

      const url = `${generateDepositSapMigrationRetryEndpoint(
        depositId,
      )}${query}`;

      const response = await fetch(url, { ...DEFAULT_POST_CONFIG });

      await isErrorResponse(response, null, dispatch);

      toastr.success('Éxito', 'Reintentar migración SAP enviado correctamente');

      dispatch(getBankReconciliationDeposits({ liquidationId }));
    } catch (error) {
      toastr.error('Error', error.message);
    }
  };

export {
  getBankReconciliations,
  clearBankReconciliations,
  getBankReconciliationLiquidations,
  clearBankReconciliationLiquidations,
  getBankReconciliationCorporates,
  clearBankReconciliationCorporates,
  getBankReconciliationCorporateManualVouchers,
  clearBankReconciliationCorporateManualVouchers,
  getBankReconciliationManualVoucherPayments,
  clearBankReconciliationManualVoucherPayments,
  retryManualVoucherPaymentSapMigration,
  getBankReconciliationDeposits,
  clearBankReconciliationDeposits,
  getBankReconciliationBankSummary,
  clearBankReconciliationBankSummary,
  retrySapMigration,
};
